import { z } from 'zod';

const optStr = (max = 500) => z.string().max(max).optional().nullable().or(z.literal(''));
const optNum = () => z.coerce.number().optional().nullable();

export const productionOrderFormSchema = z.object({
    // — Thông tin đơn sản xuất —
    productName: z.string().min(1, { message: 'Tên sản phẩm không được để trống' }).max(200),
    poReference: optStr(100),
    salesOrderId: optStr(50),
    orderDetailId: optStr(50),
    productId: optStr(50),
    customerId: optStr(50),

    // — Số lượng —
    quantity: z.coerce.number().int({ message: 'Số lượng phải là số nguyên' }).min(1, { message: 'Số lượng phải >= 1' }),
    qtyAllowance: z.coerce.number().int().min(0).optional(),
    qtyPlan: z.coerce.number().int().min(0).optional(),

    // — Kế hoạch —
    deadline: z.string().min(1, { message: 'Hạn trả hàng không được để trống' }),
    priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']),
    deploymentModel: z.enum(['INTERNAL_100', 'OUTSOURCE_100', 'HYBRID_PRINT_INTERNAL', 'HYBRID_PRINT_EXTERNAL', 'HYBRID_FINISH_EXTERNAL', 'TRANSPORT']).optional(),

    // — Kỹ thuật —
    techSpecId: optStr(50),
    layoutCode: optStr(50),

    // — Khuôn & Kẽm —
    toolings: z.array(z.object({
        toolingId: z.string(),
        toolingCode: z.string().optional().nullable(),
        toolingType: z.string(),
        status: z.string(),
        notes: z.string().optional().nullable(),
        expectedShots: z.coerce.number().optional().nullable(),
        cavityCount: z.coerce.number().optional().nullable(),
        numSets: z.coerce.number().optional().nullable(),
        platesPerSet: z.coerce.number().optional().nullable(),
        totalPlates: z.coerce.number().optional().nullable(),
        colorDescription: z.string().optional().nullable(),
        machine: z.string().optional().nullable(),
        isUserCustomized: z.boolean().optional(),
    }).passthrough()).optional(),
    
    // — Bình file —
    prepressPcsPerSheet: optNum(),
    prepressWasteAllowance: optNum(),
    prepressWastePercentage: optNum(),
    totalTargetQty: optNum(),
    totalPlanSheets: optNum(),

    // — Vật tư Giấy —
    purchasedPaperSize: optStr(100),
    printingPaperSize: optStr(100),
    cutRatio: optNum(),
    totalMaterialRequired: optNum(),
    specification: optStr(255),
    standardQuantity: optNum(),
    allowance: optNum(),

    // — Trạng thái —
    status: z.enum([
        'DRAFT', 'PENDING_APPROVAL', 'PRE_PRESS', 'READY_FOR_SCHEDULING', 'PRE_PRESS_COMPLETED',
        'MATERIALS_READY', 'PLANNED', 'RELEASED', 'IN_PROGRESS', 
        'MANUFACTURED', 'COMPLETED', 'CLOSED', 'CANCELLED', 'ON_HOLD', 'PAUSED'
    ]),
    notes: optStr(2000),

    // — Vật tư & Bài in —
    printLayouts: z.array(z.any()).optional(),
    materialPlans: z.array(z.any()).optional(),

    // — Combo Products —
    isComposite: z.boolean().default(false),
    components: z.array(z.any()).optional(),
    orderSources: z.array(z.object({
        id: z.string().optional(),
        orderDetailId: z.string(),
        salesOrderId: z.string().optional(),
        customerId: z.string().optional().nullable(),
        customerName: z.string().optional().nullable(),
        productId: z.string().optional().nullable(),
        productCode: z.string().optional().nullable(),
        productName: z.string().optional(),
        paperType: z.string().optional().nullable(),
        processes: z.any().optional(),
        dieCutId: z.string().optional().nullable(),
        quantity: z.coerce.number().min(0),
        plannedQty: z.coerce.number().min(0).optional(),
        pcsPerSheet: z.coerce.number().min(1).optional(),
        wastageSheets: z.coerce.number().min(0).optional(),
        inventoryQty: z.coerce.number().optional(),
        finishedQty: z.coerce.number().optional(),
        deliveredQty: z.coerce.number().optional(),
        progressPercent: z.coerce.number().optional(),
        isUserCustomized: z.boolean().optional(),
    }).passthrough()).optional(),
    // — Khuôn & Kẽm IDs —
    dieCutIds: z.array(z.string()).optional(),
    plateIds: z.array(z.string()).optional(),

    // — Thông số gia công & Đóng gói (Tab 5) —
    finishingSpecs: z.object({
        maMang: optStr(100),
        khoMang: optStr(50),
        slMangM: optStr(50),
        dienTichEpKim: optStr(100),
        maNhu: optStr(100),
        trangPhu: optStr(100),
        chietQuang: optStr(100),
        boi: optStr(100),
        beNoi: optStr(100),
        kieuBe: optStr(100),
        soKhuonBe: optStr(50),
        khuonBe: optStr(100),
        ghiChuBe: optStr(500),
        dan: optStr(100),
        xe: optStr(100),
        catThanhPham: optStr(100),
        thanhPhamKhac: optStr(200),
        ghiChuThanhPham: optStr(500),
        ghiChuBinhBai: optStr(500),
        ghiChuGiaoHang: optStr(500),
    }).optional(),

    actualQuantity: z.coerce.number().min(0).optional().nullable(),
});

export type ProductionOrderFormData = z.infer<typeof productionOrderFormSchema>;

export interface ProductionOrder {
    id: string;
    orderId: string;
    poReference?: string | null;
    salesOrderId?: string | null;
    orderDetailId?: string | null;
    priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
    productId?: string | null;
    productName?: string | null;
    customerId?: string | null;
    qtyOrder?: number | null;
    qtyAllowance?: number | null;
    qtyPlan?: number | null;
    techSpecs?: Record<string, unknown> | null;
    bomEstimator?: Record<string, unknown> | null;
    deploymentModel?: 'INTERNAL_100' | 'OUTSOURCE_100' | 'HYBRID_PRINT_INTERNAL' | 'HYBRID_PRINT_EXTERNAL' | 'HYBRID_FINISH_EXTERNAL' | 'TRANSPORT' | null;
    techSpecId?: string | null;
    layoutCode?: string | null;
    deadline?: string | null;
    
    prepressPcsPerSheet?: number | null;
    prepressWasteAllowance?: number | null;
    prepressWastePercentage?: number | null;
    totalTargetQty?: number | null;
    totalPlanSheets?: number | null;

    purchasedPaperSize?: string | null;
    printingPaperSize?: string | null;
    cutRatio?: number | null;
    totalMaterialRequired?: number | null;
    specification?: string | null;
    standardQuantity?: number | null;
    allowance?: number | null;
    
    actualQuantity?: number | null;
    
    status: 'DRAFT' | 'PENDING_APPROVAL' | 'PRE_PRESS' | 'READY_FOR_SCHEDULING' | 'PRE_PRESS_COMPLETED' | 'MATERIALS_READY' | 'PLANNED' | 'RELEASED' | 'IN_PROGRESS' | 'MANUFACTURED' | 'COMPLETED' | 'CLOSED' | 'CANCELLED' | 'ON_HOLD' | 'PAUSED';
    notes?: string | null;
    createdAt: string;
    updatedAt: string;

    // Relations
    salesOrder?: { id: string; orderId: string } | null;
    technicalSpec?: { id: string; specId: string; name: string } | null;
    dieCuts?: { id: string; dieCutId: string; name: string }[];
    plates?: { id: string; plateId: string; name: string }[];
    routingSteps?: unknown[];
    qcRecords?: unknown[];
}
